'use client';

import { useMemo, useState } from 'react';
import { analyzeReadability, SAMPLE_READABILITY_TEXT } from '@/lib/browser-textanalysis-tools';

type Stat = { label: string; value: string; hint?: string };

export default function BrowserReadabilityAnalyzer() {
  const [text, setText] = useState('');

  const metrics = useMemo(() => analyzeReadability(text), [text]);
  const hasInput = text.trim().length > 0;

  const stats: Stat[] = [
    {
      label: 'Flesch Reading Ease',
      value: `${metrics.fleschReadingEase}`,
      hint: '0–100, higher is easier',
    },
    {
      label: 'Flesch–Kincaid Grade',
      value: `${metrics.fleschKincaidGrade}`,
      hint: metrics.gradeLabel,
    },
    { label: 'Words', value: `${metrics.words}` },
    { label: 'Sentences', value: `${metrics.sentences}` },
    { label: 'Syllables', value: `${metrics.syllables}` },
    {
      label: 'Characters',
      value: `${metrics.characters}`,
      hint: `${metrics.charactersNoSpaces} without spaces`,
    },
    { label: 'Avg words / sentence', value: `${metrics.avgWordsPerSentence}` },
    { label: 'Avg syllables / word', value: `${metrics.avgSyllablesPerWord}` },
    { label: 'Reading time', value: metrics.readingTimeLabel, hint: '~200 wpm' },
  ];

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Readability</span>
        <h2 className="tool-live-title">Readability Analyzer</h2>
        <p className="tool-live-subtitle">
          Paste your writing to see Flesch Reading Ease, grade level, and a plain-English read on
          how easy it is to follow.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Your text</span>
              <textarea
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste or type the text you want to analyze..."
                rows={14}
                value={text}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              onClick={() => setText(SAMPLE_READABILITY_TEXT)}
              type="button"
            >
              Load sample
            </button>
            <button
              className="button button-secondary"
              disabled={!hasInput}
              onClick={() => setText('')}
              type="button"
            >
              Clear
            </button>
          </div>
        </section>

        <section className="tool-live-preview">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(8.5rem, 1fr))',
              gap: '0.6rem',
              marginBottom: '1rem',
            }}
          >
            {stats.map((stat) => (
              <div
                key={stat.label}
                style={{
                  border: '1px solid var(--border, rgba(148,163,184,0.25))',
                  borderRadius: '0.5rem',
                  padding: '0.6rem 0.7rem',
                }}
              >
                <div style={{ fontSize: '0.7rem', color: 'var(--muted, #94a3b8)' }}>
                  {stat.label}
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700 }}>{stat.value}</div>
                {stat.hint ? (
                  <div style={{ fontSize: '0.68rem', color: 'var(--muted, #94a3b8)' }}>
                    {stat.hint}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
          <div
            style={{
              border: '1px solid var(--border, rgba(148,163,184,0.25))',
              borderRadius: '0.5rem',
              padding: '0.75rem 0.85rem',
            }}
          >
            <div style={{ fontSize: '0.72rem', color: 'var(--muted, #94a3b8)' }}>
              Plain-English interpretation
            </div>
            <p style={{ margin: '0.25rem 0 0' }}>
              {hasInput ? metrics.interpretation : 'Enter some text to see how easy it is to read.'}
            </p>
          </div>
          <p className="tool-live-panel-note">Calculated locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
